import _ from 'lodash';
import RouteService from './route-service';
import api, { buildURL } from './api-service';
import { captureError } from '../pixwel/exception-handler.factory';

export const GET_SHARE_QUEUE_QUERY = `
query getShareQueue {
	session {
		id
		shareQueue {
			id
			mediaType
		}
	}
}
`;

export const CREATE_SHARE_MUTATION = `
mutation createShare($input: ShareInput) {
	createShare(input: $input) {
		share {
			id
		}
	}
}
`;

export const UPDATE_SHARE_MUTATION = `
mutation updateShare($input: ShareInput) {
	updateShare(input: $input) {
		share {
			id
		}
	}
}
`;

export const UPDATE_SHARE_QUEUE_MUTATION = `
mutation updateShareQueue($input: UserInput) {
	updateSession(input: $input) {
		session {
			id
			emailNormal
			shareQueue {
				id
				name
				thumbnail
				creative
				project {
					slug
					name
				}
				assetType {
					category
				}
			}
		}
	}
}
`;

export const ALL_SHARES_QUERY = `
query getAllShares($orderBy: String, $orderDirection: String, $limit: Int, $offset: Int) {
  shares(orderBy: $orderBy, orderDirection: $orderDirection, limit: $limit, offset: $offset) {
    totalCount
    nodes {
      id
      created
      from {
        id
        name
        emailNormal
      }
      to {
        id
        name
        emailNormal
      }
      projectNames
      assets {
        id
        name
        thumbnail
      }
      assetsCount
      expired
      advancedTags
      tags
      assetCategory
      approved
      groups {
        id
        name
      }
      notes
      languages
    }
  }
}`;

const ShareService = {

	// Recipients can enter local state via ContactPicker, ContactLists
	// or edit-preload flows. Strip empty/whitespace strings and objects
	// with no usable identity so we never send `""` or bogus entries
	// to the API (which rejects them as InvalidInvitationEmail).
	getRecipientIdentity(contact) {
		if( typeof contact === 'string' ) {
			const trimmed = contact.trim();
			return trimmed || null;
		}
		if( typeof contact === 'object' && contact ) {
			return contact.id || contact._id || contact.emailNormal || contact.email || null;
		}
		return null;
	},

	getGroupIdentity(contact) {
		if( typeof contact === 'object' && contact ) {
			return contact.id || contact._id || null;
		}
		return null;
	},

	isValidRecipient(contact) {
		return !!ShareService.getRecipientIdentity(contact);
	},

	getAllShares(params) {
		return api.graphql.request(ALL_SHARES_QUERY, params).then((data) => data.shares);
	},

	patchShare(share) {
		return api.client.patch(`/shares/${share.id}`, share);
	},

	getShareQueue(id) {
		return api.graphql.request(GET_SHARE_QUEUE_QUERY, { id });
	},

	initializeShare(data) {
		const { id, shareQueue: assets } = data.session;

		return {
			assets,
			assetCategory: assets[0]?.assetType.category,
			from: id,
			to: [],
			groups: [],
			inviteAsGuest: false,
			notes: '',
			feedback: {
				enabled: assets?.every((asset) => asset.creative === true)
			},
			tags: [],
			advancedTags: null
		};
	},

	createShare(share) {
		const assetUrls = share.assets.reduce((result, asset) => {
			result[asset.id] = RouteService.stateHref(
				'asset',
				{ projectId: asset.project.id, assetId: asset.id },
				{ absolute: true }
			);
			return result;
		}, {});

		// add 'txtd' to online tag
		const tags = share.tags.map((tag) => {
			if( tag[0] === 'online' ) {
				tag.push('txtd');
			}
			return tag;
		});

		const urls = {
			...assetUrls,
			playlist: RouteService.stateHref(
				'share-view',
				{ id: ':share' },
				{ absolute: true }
			),
			invite: RouteService.stateHref(
				share.inviteAsGuest ? 'guest-login' : 'register',
				{ code: ':code' },
				{ absolute: true }
			)
		};

		const params = {
			input: {
				...share,
				to: share.to
					.filter(ShareService.isValidRecipient)
					.map((to) => ShareService.getRecipientIdentity(to))
					.filter(Boolean),
				groups: share.groups
					.filter(ShareService.isValidRecipient)
					.map((group) => ShareService.getGroupIdentity(group))
					.filter(Boolean),
				assets: share.assets.map((asset) => asset.id),
				tags,
				urls: _.transform(urls, (result, url, key) => {
					result[key] = decodeURIComponent(url);
				}),
				languages: share?.languages?.map((language) => language.value) ?? [],
				questions: share?.questions?.map((question, index) => {
					return {
						body: question.body,
						position: index + 1
					};
				}) ?? []
			}
		};
		return api.graphql.request(CREATE_SHARE_MUTATION, params);
	},

	updateShare(share) {
		// add 'txtd' to online tag (if missing)
		const tags = share.tags?.map((tag) => {
			if( tag[0] === 'online' && !tag.includes('txtd') ) {
				tag.push('txtd');
			}
			return tag;
		});

		// Filter out questions that have an id (can't be edited by API yet)
		const questions = share.questions?.filter((question) => question.id === null).map((question) => {
			return {
				body: question.body,
				position: question.position
			};
		});

		const params = {
			input: {
				id: share.id,
				to: share.to
					.filter(ShareService.isValidRecipient)
					.map((to) => ShareService.getRecipientIdentity(to))
					.filter(Boolean),
				groups: share.groups
					.filter(ShareService.isValidRecipient)
					.map((group) => ShareService.getGroupIdentity(group))
					.filter(Boolean),
				assets: share.assets.map((asset) => asset.id),
				notes: share.notes,
				feedback: share.feedback,
				tags,
				languages: share.languages,
				advancedTags: share.advancedTags,
				questions: questions
			}
		};
		return api.graphql.request(UPDATE_SHARE_MUTATION, params);
	},

	updateShareQueue(id, assets) {
		let shareQueue = assets.map((asset) => asset.id || asset._id);

		// Filter out any null values
		shareQueue = shareQueue.filter((assetId) => !!assetId);

		return api.graphql.request(UPDATE_SHARE_QUEUE_MUTATION, {
			input: { id, shareQueue }
		});
	},

	mapRecipients(recipients) {
		const groupedRecipients = _.groupBy(
			(recipients ?? []).filter(ShareService.isValidRecipient),
			(contact) => {
				if( typeof contact === 'string' ) {
					return 'to';
				}
				if( contact?.emailNormal || contact?.email ) {
					return 'to';
				}
				return 'groups';
			}
		);

		return {
			to: groupedRecipients?.to ?? [],
			groups: groupedRecipients?.groups ?? []
		};
	},

	mapLanguagesToShares(shares, languages) {
		return shares.map((share) => {
			return {
				...share,
				languages: _.map(share.languages, (langCode) => {
					const lang = _.find(languages, { code: langCode });
					if( lang ) {
						return {
							code: lang.code,
							name: lang.name
						};
					}
					return {
						code: langCode,
						name: langCode
					};
				})
			};
		});
	},

	validateShare(share, isDownloadEnabled, isAdmin) {
		// validate recipients
		if( !share.to.length && !share.groups.length ) {
			return 'Select recipients';
		}

		if( !share.languages && !share.id && !isAdmin ) {
			return 'Select languages';
		}

		// validate download access
		if( isDownloadEnabled ) {
			let downloadInvalid;

			if( share.tags.length ) {
				downloadInvalid = share.tags.some(
					(arr) => arr.length === 1 && arr[0] !== 'online'
				);
			}
			else {
				downloadInvalid = true;
			}

			if( downloadInvalid ) {
				return 'Download access invalid';
			}
		}

		// validate voting
		if( share.feedback.enabled && !share.feedback.expires ) {
			return 'Select voting expiration';
		}

		return null;
	},

	checkTypesMatch(assets, queue) {
		const typesMatch = assets.every(
			(asset) => asset.mediaType === assets[0].mediaType
		);
		const typesAllowed =
			queue.length > 0
				? queue[0].mediaType === assets[0].mediaType
				: true;

		return typesMatch && typesAllowed;
	},

	isFeedbackEnabled(share) {
		return share.feedback && share.feedback.enabled;
	},

	isVotingClosed(share) {
		if( !share.feedback || !share.feedback.enabled ) {
			return;
		}

		return share.feedback.expires <= Math.floor(Date.now() / 1000);
	},

	isQuestionsSubmitted(share) {
		if( !share.questions?.length ) {
			return true;
		}
		return share.questions.every((question) => !!question.response);
	},

	isShareExpired(share) {
		return !!share.expired;
	},

	isVoteSubmitted(share) {
		return share.assets.every((asset) => {
			if( !asset.$feedback.vote ) {
				return false;
			}

			return asset.$feedback.vote.submitted;
		});
	},

	expireVoting(share) {
		if(!share.feedback || !share.feedback.enabled) {
			throw new Error('only shares with feedback enabled can have an expiration value');
		}
		share.feedback.expires = Math.floor(Date.now() / 1000);

		return api.client.patch(`Shares/${share._id}`, {
			feedback: share.feedback
		}).then((res) => res.data);
	},

	expireShare(share) {
		return api.client.patch(`/Shares/${share._id}`, {
			expired: true
		}).then((res) => res.data);
	},

	getFeedback(assets) {
		return _.keyBy(assets.map((asset) => {
			return {
				asset: asset._id,
				...asset.$feedback.vote,
				$links: {
					self: asset.$links.assetFeedback
				}
			};
		}), 'asset');
	},

	getShareById(shareId) {
		return api.client.get(buildURL('/shares', {
			id: shareId
		}))
			.then((res) => {
				return res.data[0] || res.data;
			});
	},

	getSharesByQueryAndPage({ query, start, end }) {
		let params = {
			user: query.user,
			order: {
				_id: query.order._id
			},
			created: {
				from: query.created?.from
			}
		};
		if(query.from) {
			params.from = query.from;
		}
		else {
			params.to = true;
		}

		return api.client.get(
			buildURL('/shares', params),
			{ headers: { 'X-Range': `resources=${start}-${end}` } }
		);
	},

	getSharesByQuery(query) {
		return api.client.get(buildURL('/shares', query), { headers: { 'X-Range': 'resources=0-500' } })
			.then((res) => res.data);
	},

	submitQuestions(shareId, questions) {
		const payload = questions?.map((question) => {
			return {
				_id: question._id,
				response: question.response?.body || question.response
			};
		}) || [];

		return api.client.patch(`/shares/${shareId}`, {
			questions: payload
		});
	},

	filterWaitingResponseUsers(recipients, questions) {
		return recipients?.filter((recipient) => {
			const recipientId = recipient.id ?? recipient._id;
			return !questions?.some((question) => {
				return question.responses?.some(
					(response) => (response.user.id ?? response.user._id) === recipientId
				);
			});
		});
	},

	submitFeedback(allFeedback) {

		const unvotedFeedback = Object.values(allFeedback);

		return unvotedFeedback.reduce((chain, feedback) => {
			return chain.then(() => {
				const { comment, vote, wouldOrder, aspectRatios } = feedback;
				return api.client.patch(`/Assetfeedback/${feedback._id}`, {
					comment,
					vote,
					wouldOrder,
					submitted: true,
					aspectRatios
				});
			}).catch((err) => {
				let errorMessage = 'An error occurred saving feedback';
				if( err.data?.message?.includes('Forbidden') ) {
					errorMessage = 'You do not have permission to change your vote. Voting is closed or your permission has been removed.';
				}
				captureError(err, errorMessage);
			});
		}, Promise.resolve());
	},

	deleteShare(share) {
		let id = typeof share === 'object' ? share.id ?? share?._id : share;
		return api.client.delete(`/shares/${id}`);
	}
};

export default ShareService;
